import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix für Leaflet Default-Icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DirecterzeugerMap = ({ direkterzeuger, onMarkerClick, onRightClick, center, zoom, highlight }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Karte initialisieren
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([51.4556, 7.0116], 8);

      // OpenStreetMap Tiles wie in der Android App
      L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors. Tiles courtesy of Humanitarian OpenStreetMap Team',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);

      // Rechtsklick-Event für neuen Hof
      mapInstanceRef.current.on('contextmenu', (e) => {
        if (onRightClick) {
          onRightClick(e.latlng.lat, e.latlng.lng);
        }
      });
    }

    // Alle Marker entfernen
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        mapInstanceRef.current.removeLayer(layer);
      }
    });

    if (direkterzeuger.length === 0 && !highlight) return;

    // Marker hinzufügen
    const markers = [];
    direkterzeuger.forEach((hof) => {
      const marker = L.marker([hof.latitude, hof.longitude]);
      
      // Icon basierend auf Membership
      const isPremium = hof.membership === 'premium';
      if (isPremium) {
        const premiumIcon = L.divIcon({
          html: '<div style="background: #569F39; border: 2px solid #fff; border-radius: 50%; width: 20px; height: 20px;"></div>',
          className: 'premium-marker',
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        });
        marker.setIcon(premiumIcon);
      }

      // Popup mit Hofdetails
      const popupContent = `
        <div style="min-width: 200px;">
          <h4 style="margin: 0 0 8px 0; color: #569F39;">${hof.name}</h4>
          <p style="margin: 4px 0;"><strong>Stadt:</strong> ${hof.city}</p>
          <p style="margin: 4px 0;"><strong>PLZ:</strong> ${hof.plz}</p>
          <p style="margin: 4px 0;"><strong>Mitgliedschaft:</strong> ${hof.membership || 'Standard'}</p>
          <button 
            onclick="window.openHofProfile('${hof.id}')" 
            style="margin-top: 8px; padding: 4px 8px; background: #569F39; color: white; border: none; border-radius: 4px; cursor: pointer;"
          >
            Details anzeigen
          </button>
        </div>
      `;
      
      marker.bindPopup(popupContent);
      marker.addTo(mapInstanceRef.current);
      markers.push([hof.latitude, hof.longitude]);
    });

    // Highlight-Marker (z. B. Suggestion-Position) in hellem Grün
    if (highlight && typeof highlight.latitude === 'number' && typeof highlight.longitude === 'number') {
      const highlightIcon = L.divIcon({
        html: '<div style="background:#A7F3D0; border: 2px solid #fff; border-radius: 50%; width: 22px; height: 22px; box-shadow: 0 0 0 2px #34D399;"></div>',
        className: 'highlight-marker',
        iconSize: [22, 22],
        iconAnchor: [11, 11]
      });
      const hl = L.marker([highlight.latitude, highlight.longitude], { icon: highlightIcon, zIndexOffset: 1000 });
      hl.bindPopup('<strong>Vorschlag</strong>');
      hl.addTo(mapInstanceRef.current);
      markers.push([highlight.latitude, highlight.longitude]);
    }

    // View setzen: bevorzugt auf übergebenes center/zoom, sonst auf Marker-Bounds
    if (Array.isArray(center) && typeof center[0] === 'number' && typeof center[1] === 'number' && typeof zoom === 'number') {
      mapInstanceRef.current.setView(center, zoom);
    } else if (markers.length > 0) {
      const markerLayers = [];
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          markerLayers.push(layer);
        }
      });
      if (markerLayers.length > 0) {
        const group = new L.featureGroup(markerLayers);
        mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
      }
    }

    // Globale Funktion für Popup-Buttons
    window.openHofProfile = (hofId) => {
      if (onMarkerClick) {
        onMarkerClick(hofId);
      }
    };

    return () => {
      if (window.openHofProfile) {
        delete window.openHofProfile;
      }
    };
  }, [direkterzeuger, onMarkerClick, center, zoom, highlight]);

  useEffect(() => {
    // Cleanup beim Unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return <div ref={mapRef} style={{ height: '500px', width: '100%' }} />;
};

export default DirecterzeugerMap;
